import React, { memo } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Globe } from 'lucide-react';
import { Area, AreaChart, XAxis, YAxis } from 'recharts';
import { ChartContainer, ChartTooltip } from '@/components/ui/chart';

interface TrendData {
  monthly_data: Array<{
    year: number;
    month: string;
    volume: number;
  }>;
  avg_monthly_searches: number;
}

interface KeywordRowProps {
  keyword: {
    id: string | number;
    keyword_text: string;
    volume?: number;
    da_score?: number;
    low_cpc?: number;
    high_cpc?: number;
    category_display?: string;
    trend?: any;
  };
  isSelected: boolean;
  hasDAScore: boolean;
  isBeingAnalyzed: boolean;
  onSelectKeyword: (keywordId: string, checked: boolean) => void;
}

const TrendChart: React.FC<{ trendData: TrendData }> = React.memo(({ trendData }) => {
  if (!trendData?.monthly_data) {
    return (
      <div className="w-32 h-16 flex items-center justify-center text-xs text-muted-foreground">
        No trend data
      </div>
    );
  }

  const chartData = trendData.monthly_data.map(item => ({
    name: `${item.month} ${item.year}`,
    volume: item.volume,
  }));

  const config = {
    volume: {
      label: "Volume",
      color: "hsl(var(--chart-1))",
    },
  };

  return (
    <div className="w-32 h-16">
      <ChartContainer config={config}>
        <AreaChart data={chartData}>
          <defs>
            <linearGradient id="volumeGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <XAxis 
            dataKey="name" 
            hide={true}
          />
          <YAxis 
            hide={true}
          />
          <ChartTooltip
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                return (
                  <div className="rounded-lg border bg-background p-2 shadow-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-primary"></div>
                      <span className="font-medium text-foreground">
                        {payload[0].value?.toLocaleString()}
                      </span>
                    </div>
                  </div>
                )
              }
              return null
            }}
          />
          <Area
            type="monotone"
            dataKey="volume"
            stroke="hsl(var(--primary))"
            fill="url(#volumeGradient)"
            strokeWidth={2}
            strokeOpacity={1}
          />
        </AreaChart>
      </ChartContainer>
    </div>
  );
});

const KeywordRow = memo<KeywordRowProps>(({
  keyword,
  isSelected,
  hasDAScore,
  isBeingAnalyzed,
  onSelectKeyword
}) => {
  const handleCheckboxChange = (checked: boolean) => {
    onSelectKeyword(keyword.id.toString(), checked);
  };

  const handleGoogleSearch = () => {
    const searchUrl = `https://www.google.com/search?q=${encodeURIComponent(keyword.keyword_text)}`;
    window.open(searchUrl, '_blank');
  };

  return (
    <div
      key={keyword.id}
      className="relative flex items-center gap-4 py-4 px-2"
    >
      <div className="w-4 flex items-center justify-center">
        <Checkbox
          className="z-1000"
          checked={isSelected}
          onCheckedChange={handleCheckboxChange}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
      <div className="w-[18%] min-w-0">
        <div className="font-medium text-sm text-foreground w-full">{keyword.keyword_text}</div>
      </div>
      <div className="w-[12%] min-w-0 text-sm text-muted-foreground">
        <div className="w-full">{keyword.volume?.toLocaleString() || 'N/A'}</div>
      </div>
      <div className="w-[10%] min-w-0">
        {hasDAScore ? (
          <span className="text-sm font-medium text-foreground w-full block">
            {keyword.da_score}
          </span>
        ) : (
          <div className={`w-full h-4 rounded ${isBeingAnalyzed ? 'bg-gradient-to-r from-primary/5 via-primary/10 to-primary/2 animate-pulse' : 'bg-muted/20 animate-pulse'}`} style={isBeingAnalyzed ? { animationDuration: '1s' } : undefined}></div>
        )}
      </div>
      <div className="w-[15%] min-w-0 text-sm text-muted-foreground">
        <div className="w-full">{hasDAScore ? (
          `$${keyword.low_cpc} - $${keyword.high_cpc}`
        ) : (
          <div className={`w-full h-4 rounded ${isBeingAnalyzed ? 'bg-gradient-to-r from-primary/5 via-primary/10 to-primary/2 animate-pulse' : 'bg-muted/20 animate-pulse'}`} style={isBeingAnalyzed ? { animationDuration: '1s' } : undefined}></div>
        )}</div>
      </div>
      <div className="w-[20%] min-w-0 text-center">
        {keyword.category_display && hasDAScore ? (
          <Badge variant="outline" className="text-xs w-full">
            {keyword.category_display}
          </Badge>
        ) : (
          <div className={`w-full h-4 rounded ${isBeingAnalyzed ? 'bg-gradient-to-r from-primary/5 via-primary/10 to-primary/2 animate-pulse' : 'bg-muted/20 animate-pulse'}`} style={isBeingAnalyzed ? { animationDuration: '1s' } : undefined}></div>
        )}
      </div>
      <div className="w-[25%] min-w-0 flex items-center justify-center">
        <div className="w-full">
          {keyword.trend ? (
            <TrendChart trendData={keyword.trend} />
          ) : (
            <div className={`w-full h-8 rounded ${isBeingAnalyzed ? 'bg-gradient-to-r from-primary/5 via-primary/10 to-primary/2 animate-pulse' : 'bg-muted/20 animate-pulse'}`} style={isBeingAnalyzed ? { animationDuration: '1s' } : undefined}></div>
          )}
        </div>
      </div>
      <div className="w-[10%] min-w-0 flex gap-1 items-center justify-center space-x-1">
        <Button
          variant="outline"
          size="sm"
          className="h-8 px-2"
          onClick={handleGoogleSearch}
        >
          <Globe className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
});

KeywordRow.displayName = 'KeywordRow';

export default KeywordRow;
